import { ref } from 'vue'
import { defineStore } from 'pinia'
import { useAuthStore } from '@/stores/auth'
import type { DashboardResponse, BaseAnalysis } from '@/stores/dashboards'

export type Organization = {
  id: string
  name: string
  slug: string
  stytch_organization_id: string | null
  logo_url: string | null
  is_active: boolean
  latest_member_login_at: string | null
  client_name: string | null
  reports_enabled: boolean
}

export type OrganizationMember = {
  member_id: string
  email: string
  name: string
  status: string
  last_login_at: string | null
}

export type Homepage = {
  id: string
  notion_page_id: string
  organization_id: string
  notion_host: string
}

export type Platform = {
  id: string
  code: string
  name: string
  parent_platform_id: string | null
  available_methods: string[]
  default_method: string
  fallback_method: string | null
  advertiser_id_pattern: string | null
  advertiser_id_hint: string | null
  ad_library_url_template: string | null
  ad_library_requires_date_range: boolean
  ad_library_extra_params: string | null
  biz_id_extra_kwargs_field: string | null
}

export type Country = {
  id: string
  code: string
  name_en: string
  name_fr: string
  emoji: string
}

export type CountryGroup = {
  id: string
  code: string
  name: string
  group_type: string | null
  country_ids: string[]
}

export type Sector = {
  id: string
  name: string
  slug: string
  emoji: string
  logo_url: string | null
  brand_count: number
}

export type BrandSectorInfo = {
  id: string
  name: string
  emoji: string
}

export type Brand = {
  id: string
  name: string
  slug: string
  logo_url: string | null
  website: string | null
  platform_codes: string[]
  advertiser_count: number
  sectors: BrandSectorInfo[]
  subscribed_org_slugs: string[]
}

export type PlatformSubscription = {
  id: string
  platform_id: string
  platform_code: string
  platform_name: string
  is_active: boolean
  is_visible: boolean
}

export type CountrySubscription = {
  id: string
  country_id: string
  country_code: string
  country_name: string
  is_active: boolean
  is_visible: boolean
}

export type BrandSubscription = {
  id: string
  brand_id: string
  brand_name: string
  brand_slug: string
  is_owner: boolean
  is_active: boolean
  is_visible: boolean
}

// source "brand" = inherited from brand (not excluded), "advertiser" = explicitly linked
export type AdvertiserSectorInfo = BrandSectorInfo & { source: 'brand' | 'advertiser' }

export type PlatformAdvertiser = {
  id: string
  brand_id: string
  platform_id: string
  platform_code: string
  platform_name: string
  platform_advertiser_identifier: string
  country_code: string | null
  country_name: string | null
  extra_kwargs: Record<string, unknown> | null
  search_type: 'keyword' | 'advertiser_name'
  sectors: AdvertiserSectorInfo[]
}

export type CreatePlatformAdvertiserRequest = {
  platform_id: string
  platform_advertiser_identifier: string
  country_code?: string | null
  extra_kwargs?: Record<string, unknown> | null
  search_type?: 'keyword' | 'advertiser_name'
}

export type UpdatePlatformAdvertiserRequest = {
  platform_advertiser_identifier?: string
  country_code?: string | null
  extra_kwargs?: Record<string, unknown> | null
  search_type?: 'keyword' | 'advertiser_name'
}

type ErrorResponse = {
  detail: string
}

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || ''

// ponytail: 5min TTL for reference data that rarely changes
const CACHE_TTL_MS = 5 * 60 * 1000

export const useAdmin = defineStore('admin', () => {
  const auth = useAuthStore()
  const organizations = ref<Organization[]>([])
  const platforms = ref<Platform[]>([])
  const countries = ref<Country[]>([])
  const countryGroups = ref<CountryGroup[]>([])
  const brands = ref<Brand[]>([])
  const sectors = ref<Sector[]>([])

  // Cache timestamps for reference data
  const cache = {
    platforms: 0,
    countries: 0,
    countryGroups: 0,
    sectors: 0,
    organizations: 0,
    brands: 0,
  }

  function isCacheValid(key: keyof typeof cache): boolean {
    return cache[key] > 0 && Date.now() - cache[key] < CACHE_TTL_MS
  }

  function invalidateCache(key: keyof typeof cache) {
    cache[key] = 0
  }

  async function fetchOrganization(organizationId: string): Promise<Organization> {
    return await fetch(`${BACKEND_URL}/api/admin/organizations/${organizationId}`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
      },
    }).then(async (res) => {
      const data = await res.json()
      if (!res.ok) {
        throw new Error((data as ErrorResponse).detail || 'Error fetching organization')
      }
      return data as Organization
    })
  }

  async function fetchOrganizations() {
    if (isCacheValid('organizations')) return
    return await fetch(`${BACKEND_URL}/api/admin/organizations`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
      },
    })
      .then(async (res) => {
        const data = await res.json()
        if (!res.ok) {
          throw new Error((data as ErrorResponse).detail || 'Error fetching dashboards')
        }
        organizations.value = data as Organization[]
        cache.organizations = Date.now()
      })
      .catch((err) => {
        console.log(`Error: ${err.message}`)
      })
  }

  async function createOrganization({
    name,
    slug,
    client_name,
  }: {
    name: string
    slug: string
    client_name?: string
  }): Promise<Organization> {
    return await fetch(`${BACKEND_URL}/api/admin/organizations`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify({ name, slug, client_name }),
    }).then(async (res) => {
      let data = await res.json()
      if (!res.ok) {
        throw new Error((data as ErrorResponse).detail || 'Error creating organization')
      }
      data = data as Organization
      organizations.value.push(data)
      return data
    })
  }

  async function updateOrganization(
    organizationId: string,
    {
      name,
      slug,
      client_name,
      reports_enabled,
    }: { name?: string; slug?: string; client_name?: string; reports_enabled?: boolean },
  ): Promise<void> {
    return await fetch(`${BACKEND_URL}/api/admin/organizations/${organizationId}`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
        'Content-Type': 'application/json',
      },
      method: 'PATCH',
      body: JSON.stringify({ name, slug, client_name, reports_enabled }),
    }).then(async (res) => {
      const data = await res.json()
      if (!res.ok) {
        throw new Error((data as ErrorResponse).detail || 'Error updating organization')
      }
      // update the organization in the local state
      organizations.value = organizations.value.map((org) =>
        org.id === organizationId ? (data as Organization) : org,
      )
    })
  }

  async function deleteOrganization(organizationId: string): Promise<void> {
    return await fetch(`${BACKEND_URL}/api/admin/organizations/${organizationId}`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
      },
      method: 'DELETE',
    }).then(async (res) => {
      if (!res.ok) {
        const data = await res.json()
        throw new Error((data as ErrorResponse).detail || 'Error deleting organization')
      }
      // Remove from local state
      organizations.value = organizations.value.filter((org) => org.id !== organizationId)
    })
  }

  async function toggleOrganizationActive(
    organizationId: string,
    isActive: boolean,
  ): Promise<void> {
    // Fail fast if org not found
    const targetOrg = organizations.value.find((org) => org.id === organizationId)
    if (!targetOrg) throw new Error(`Organization ${organizationId} not found`)
    const previousState = targetOrg.is_active

    // Optimistically update local state
    organizations.value = organizations.value.map((org) =>
      org.id === organizationId ? { ...org, is_active: isActive } : org,
    )

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/organizations/${organizationId}`, {
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${auth.stytchToken}`,
          'Content-Type': 'application/json',
        },
        method: 'PATCH',
        body: JSON.stringify({ is_active: isActive }),
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error((data as ErrorResponse).detail || 'Error toggling organization status')
      }
    } catch (error) {
      // Rollback on failure
      organizations.value = organizations.value.map((org) =>
        org.id === organizationId ? { ...org, is_active: previousState } : org,
      )
      throw error
    }
  }

  async function fetchOrganizationDashboards(organizationId: string): Promise<DashboardResponse> {
    return await fetch(`${BACKEND_URL}/api/admin/organizations/${organizationId}/dashboards`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
      },
    }).then(async (res) => {
      const data = await res.json()
      if (!res.ok) {
        throw new Error((data as ErrorResponse).detail || 'Error fetching organization dashboards')
      }
      return data as DashboardResponse
    })
  }

  async function updateOrganizationDashboards(
    organizationId: string,
    analyses: BaseAnalysis[],
  ): Promise<DashboardResponse> {
    return await fetch(`${BACKEND_URL}/api/admin/organizations/${organizationId}/dashboards`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify({ analyses }),
    }).then(async (res) => {
      const data = await res.json()
      if (!res.ok) {
        throw new Error((data as ErrorResponse).detail || 'Error updating organization dashboards')
      }
      return data as DashboardResponse
    })
  }

  async function saveOrganizationLogo(organizationId: string, logo_file: File): Promise<void> {
    const formData = new FormData()
    formData.append('logo_file', logo_file)

    return await fetch(`${BACKEND_URL}/api/admin/organizations/${organizationId}/logo`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
      },
      method: 'POST',
      body: formData,
    }).then(async (res) => {
      const data = await res.json()
      if (!res.ok) {
        throw new Error((data as ErrorResponse).detail || 'Error saving organization logo')
      }
      // update the organization in the local state
      organizations.value = organizations.value.map((org) =>
        org.id === organizationId ? (data as Organization) : org,
      )
    })
  }

  async function deleteOrganizationLogo(organizationId: string): Promise<void> {
    return await fetch(`${BACKEND_URL}/api/admin/organizations/${organizationId}/logo`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
      },
      method: 'DELETE',
    }).then(async (res) => {
      const data = await res.json().catch(() => null)
      if (!res.ok) {
        throw new Error(
          (data as ErrorResponse | null)?.detail || 'Error deleting organization logo',
        )
      }
      // update the organization in the local state
      if (data) {
        organizations.value = organizations.value.map((org) =>
          org.id === organizationId ? (data as Organization) : org,
        )
      } else {
        organizations.value = organizations.value.map((org) =>
          org.id === organizationId ? { ...org, logo_url: null } : org,
        )
      }
    })
  }

  async function inviteUsers(
    organizationId: string,
    users: { email: string; name: string }[],
  ): Promise<void> {
    return await fetch(`${BACKEND_URL}/api/admin/organizations/${organizationId}/invite-users`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify({ users }),
    }).then(async (res) => {
      const data = await res.json()
      if (!res.ok) {
        throw new Error((data as ErrorResponse).detail || 'Error inviting users')
      }
      // Check for individual invite failures in the response
      const results = (
        data as { results: Array<{ email: string; success: boolean; error?: string }> }
      ).results
      const failures = results.filter((r) => !r.success)
      if (failures.length > 0) {
        const errorMessages = failures
          .map((f) => `${f.email}: ${f.error || 'Unknown error'}`)
          .join(', ')
        throw new Error(errorMessages)
      }
    })
  }

  async function fetchOrganizationMembers(organizationId: string): Promise<OrganizationMember[]> {
    return await fetch(`${BACKEND_URL}/api/admin/organizations/${organizationId}/members`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
      },
    }).then(async (res) => {
      const data = await res.json()
      if (!res.ok) {
        throw new Error((data as ErrorResponse).detail || 'Error fetching organization members')
      }
      return (data as { members: OrganizationMember[] }).members
    })
  }

  async function deleteOrganizationMember(organizationId: string, memberId: string): Promise<void> {
    return await fetch(
      `${BACKEND_URL}/api/admin/organizations/${organizationId}/members/${memberId}`,
      {
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${auth.stytchToken}`,
        },
        method: 'DELETE',
      },
    ).then(async (res) => {
      const data = await res.json()
      if (!res.ok) {
        throw new Error((data as ErrorResponse).detail || 'Error deleting member')
      }
    })
  }

  async function resendMemberInvite(organizationId: string, memberId: string): Promise<void> {
    return await fetch(
      `${BACKEND_URL}/api/admin/organizations/${organizationId}/members/${memberId}/resend-invite`,
      {
        credentials: 'include',
        headers: { Authorization: `Bearer ${auth.stytchToken}` },
        method: 'POST',
      },
    ).then(async (res) => {
      if (!res.ok) {
        const data = await res.json()
        throw new Error((data as ErrorResponse).detail || 'Error resending invite')
      }
    })
  }

  async function fetchOrganizationHomepage(organizationId: string): Promise<Homepage | null> {
    return await fetch(`${BACKEND_URL}/api/admin/organizations/${organizationId}/homepage`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
      },
    }).then(async (res) => {
      const data = await res.json()
      if (!res.ok) {
        throw new Error((data as ErrorResponse).detail || 'Error fetching homepage')
      }
      return data as Homepage | null
    })
  }

  async function updateOrganizationHomepage(
    organizationId: string,
    notionPageId: string,
  ): Promise<Homepage> {
    return await fetch(`${BACKEND_URL}/api/admin/organizations/${organizationId}/homepage`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify({ notion_page_id: notionPageId }),
    }).then(async (res) => {
      const data = await res.json()
      if (!res.ok) {
        throw new Error((data as ErrorResponse).detail || 'Error updating homepage')
      }
      return data as Homepage
    })
  }

  async function deleteOrganizationHomepage(organizationId: string): Promise<void> {
    return await fetch(`${BACKEND_URL}/api/admin/organizations/${organizationId}/homepage`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
      },
      method: 'DELETE',
    }).then(async (res) => {
      if (!res.ok) {
        const data = await res.json()
        throw new Error((data as ErrorResponse).detail || 'Error deleting homepage')
      }
    })
  }

  // =========================================================================
  // Data Entities - Platforms, Countries, Brands
  // =========================================================================

  async function fetchPlatforms(): Promise<Platform[]> {
    if (isCacheValid('platforms')) return platforms.value
    return await fetch(`${BACKEND_URL}/api/admin/platforms`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
      },
    }).then(async (res) => {
      const data = await res.json()
      if (!res.ok) {
        throw new Error((data as ErrorResponse).detail || 'Error fetching platforms')
      }
      platforms.value = data as Platform[]
      cache.platforms = Date.now()
      return platforms.value
    })
  }

  // Get subplatforms for a given parent platform ID
  function getSubplatforms(parentId: string): Platform[] {
    return platforms.value.filter((p) => p.parent_platform_id === parentId)
  }

  // Get all codes for a platform family (root + children) for filtering
  function getPlatformFamilyCodes(rootCode: string): string[] {
    const root = platforms.value.find((p) => p.code === rootCode && !p.parent_platform_id)
    if (!root) return [rootCode]
    const subs = getSubplatforms(root.id)
    return [root.code, ...subs.map((s) => s.code)]
  }

  async function fetchCountries(): Promise<Country[]> {
    if (isCacheValid('countries')) return countries.value
    return await fetch(`${BACKEND_URL}/api/admin/countries`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
      },
    }).then(async (res) => {
      const data = await res.json()
      if (!res.ok) {
        throw new Error((data as ErrorResponse).detail || 'Error fetching countries')
      }
      countries.value = data as Country[]
      cache.countries = Date.now()
      return countries.value
    })
  }

  async function fetchCountryGroups(): Promise<CountryGroup[]> {
    if (isCacheValid('countryGroups')) return countryGroups.value
    return await fetch(`${BACKEND_URL}/api/admin/country-groups`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
      },
    }).then(async (res) => {
      const data = await res.json()
      if (!res.ok) {
        throw new Error((data as ErrorResponse).detail || 'Error fetching country groups')
      }
      countryGroups.value = data as CountryGroup[]
      cache.countryGroups = Date.now()
      return countryGroups.value
    })
  }

  async function fetchBrands(): Promise<Brand[]> {
    if (isCacheValid('brands')) return brands.value
    return await fetch(`${BACKEND_URL}/api/admin/brands`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
      },
    }).then(async (res) => {
      const data = await res.json()
      if (!res.ok) {
        throw new Error((data as ErrorResponse).detail || 'Error fetching brands')
      }
      brands.value = data as Brand[]
      cache.brands = Date.now()
      return brands.value
    })
  }

  async function fetchBrand(brandId: string): Promise<Brand> {
    return await fetch(`${BACKEND_URL}/api/admin/brands/${brandId}`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
      },
    }).then(async (res) => {
      const data = await res.json()
      if (!res.ok) {
        throw new Error((data as ErrorResponse).detail || 'Error fetching brand')
      }
      return data as Brand
    })
  }

  async function createBrand({
    name,
    slug,
    website,
  }: {
    name: string
    slug?: string
    website?: string | null
  }): Promise<Brand> {
    return await fetch(`${BACKEND_URL}/api/admin/brands`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify({ name, slug: slug || null, website: website || null }),
    }).then(async (res) => {
      const data = await res.json()
      if (!res.ok) {
        throw new Error((data as ErrorResponse).detail || 'Error creating brand')
      }
      const brand = data as Brand
      brands.value.push(brand)
      // Re-sort by name
      brands.value.sort((a, b) => a.name.localeCompare(b.name))
      return brand
    })
  }

  async function updateBrand(
    brandId: string,
    updates: { name?: string; website?: string | null },
  ): Promise<Brand> {
    return await fetch(`${BACKEND_URL}/api/admin/brands/${brandId}`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
        'Content-Type': 'application/json',
      },
      method: 'PATCH',
      body: JSON.stringify(updates),
    }).then(async (res) => {
      const data = await res.json()
      if (!res.ok) {
        throw new Error((data as ErrorResponse).detail || 'Error updating brand')
      }
      const updatedBrand = data as Brand
      brands.value = brands.value.map((b) => (b.id === brandId ? updatedBrand : b))
      brands.value.sort((a, b) => a.name.localeCompare(b.name))
      return updatedBrand
    })
  }

  async function addBrandSector(brandId: string, sectorId: string): Promise<Brand> {
    return await fetch(`${BACKEND_URL}/api/admin/brands/${brandId}/sectors/${sectorId}`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
      },
      method: 'POST',
    }).then(async (res) => {
      const data = await res.json()
      if (!res.ok) {
        throw new Error((data as ErrorResponse).detail || 'Error adding sector to brand')
      }
      const updatedBrand = data as Brand
      brands.value = brands.value.map((b) => (b.id === brandId ? updatedBrand : b))
      return updatedBrand
    })
  }

  async function removeBrandSector(brandId: string, sectorId: string): Promise<Brand> {
    return await fetch(`${BACKEND_URL}/api/admin/brands/${brandId}/sectors/${sectorId}`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
      },
      method: 'DELETE',
    }).then(async (res) => {
      const data = await res.json()
      if (!res.ok) {
        throw new Error((data as ErrorResponse).detail || 'Error removing sector from brand')
      }
      const updatedBrand = data as Brand
      brands.value = brands.value.map((b) => (b.id === brandId ? updatedBrand : b))
      return updatedBrand
    })
  }

  async function deleteBrand(brandId: string): Promise<void> {
    return await fetch(`${BACKEND_URL}/api/admin/brands/${brandId}`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
      },
      method: 'DELETE',
    }).then(async (res) => {
      if (!res.ok) {
        const data = await res.json()
        throw new Error((data as ErrorResponse).detail || 'Error deleting brand')
      }
      brands.value = brands.value.filter((b) => b.id !== brandId)
    })
  }

  async function saveBrandLogo(brandId: string, logoFile: File): Promise<Brand> {
    const formData = new FormData()
    formData.append('logo_file', logoFile)

    return await fetch(`${BACKEND_URL}/api/admin/brands/${brandId}/logo`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
      },
      method: 'POST',
      body: formData,
    }).then(async (res) => {
      const data = await res.json()
      if (!res.ok) {
        throw new Error((data as ErrorResponse).detail || 'Error saving brand logo')
      }
      const updatedBrand = data as Brand
      brands.value = brands.value.map((b) =>
        b.id === brandId ? { ...b, logo_url: updatedBrand.logo_url } : b,
      )
      return updatedBrand
    })
  }

  async function deleteBrandLogo(brandId: string): Promise<Brand> {
    return await fetch(`${BACKEND_URL}/api/admin/brands/${brandId}/logo`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
      },
      method: 'DELETE',
    }).then(async (res) => {
      const data = await res.json().catch(() => null)
      if (!res.ok) {
        throw new Error((data as ErrorResponse | null)?.detail || 'Error deleting brand logo')
      }
      const currentBrand = brands.value.find((b) => b.id === brandId)
      if (!data && !currentBrand) {
        throw new Error(`Brand ${brandId} not found in local state`)
      }
      const updatedBrand = data ? (data as Brand) : { ...currentBrand!, logo_url: null }
      brands.value = brands.value.map((b) => (b.id === brandId ? updatedBrand : b))
      return updatedBrand
    })
  }

  async function importMetaLogo(brandId: string): Promise<Brand> {
    return await fetch(`${BACKEND_URL}/api/admin/brands/${brandId}/import-meta-logo`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
      },
      method: 'POST',
    }).then(async (res) => {
      const data = await res.json()
      if (!res.ok) {
        throw new Error((data as ErrorResponse).detail || 'Error importing logo from Meta')
      }
      const updatedBrand = data as Brand
      brands.value = brands.value.map((b) =>
        b.id === brandId ? { ...b, logo_url: updatedBrand.logo_url } : b,
      )
      return updatedBrand
    })
  }

  // =========================================================================
  // Sectors
  // =========================================================================

  async function fetchSectors(): Promise<Sector[]> {
    if (isCacheValid('sectors')) return sectors.value
    return await fetch(`${BACKEND_URL}/api/admin/sectors`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
      },
    }).then(async (res) => {
      const data = await res.json()
      if (!res.ok) {
        throw new Error((data as ErrorResponse).detail || 'Error fetching sectors')
      }
      sectors.value = data as Sector[]
      cache.sectors = Date.now()
      return sectors.value
    })
  }

  async function createSector({
    name,
    slug,
    emoji,
  }: {
    name: string
    slug?: string
    emoji?: string
  }): Promise<Sector> {
    return await fetch(`${BACKEND_URL}/api/admin/sectors`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify({ name, slug: slug || null, emoji: emoji || '' }),
    }).then(async (res) => {
      const data = await res.json()
      if (!res.ok) {
        throw new Error((data as ErrorResponse).detail || 'Error creating sector')
      }
      const sector = data as Sector
      sectors.value.push(sector)
      sectors.value.sort((a, b) => a.name.localeCompare(b.name))
      return sector
    })
  }

  async function updateSector(
    sectorId: string,
    { name, emoji }: { name?: string; emoji?: string },
  ): Promise<Sector> {
    return await fetch(`${BACKEND_URL}/api/admin/sectors/${sectorId}`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
        'Content-Type': 'application/json',
      },
      method: 'PATCH',
      body: JSON.stringify({ name, emoji }),
    }).then(async (res) => {
      const data = await res.json()
      if (!res.ok) {
        throw new Error((data as ErrorResponse).detail || 'Error updating sector')
      }
      const updatedSector = data as Sector
      sectors.value = sectors.value.map((s) => (s.id === sectorId ? updatedSector : s))
      sectors.value.sort((a, b) => a.name.localeCompare(b.name))
      return updatedSector
    })
  }

  async function deleteSector(sectorId: string): Promise<void> {
    return await fetch(`${BACKEND_URL}/api/admin/sectors/${sectorId}`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
      },
      method: 'DELETE',
    }).then(async (res) => {
      if (!res.ok) {
        const data = await res.json()
        throw new Error((data as ErrorResponse).detail || 'Error deleting sector')
      }
      sectors.value = sectors.value.filter((s) => s.id !== sectorId)
    })
  }

  async function fetchSectorBrands(sectorId: string): Promise<Brand[]> {
    return await fetch(`${BACKEND_URL}/api/admin/sectors/${sectorId}/brands`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
      },
    }).then(async (res) => {
      const data = await res.json()
      if (!res.ok) {
        throw new Error((data as ErrorResponse).detail || 'Error fetching sector brands')
      }
      return data as Brand[]
    })
  }

  // =========================================================================
  // Platform Advertisers
  // =========================================================================

  async function fetchBrandAdvertisers(brandId: string): Promise<PlatformAdvertiser[]> {
    return await fetch(`${BACKEND_URL}/api/admin/brands/${brandId}/advertisers`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
      },
    }).then(async (res) => {
      const data = await res.json()
      if (!res.ok) {
        throw new Error((data as ErrorResponse).detail || 'Error fetching brand advertisers')
      }
      return data as PlatformAdvertiser[]
    })
  }

  async function fetchAllAdvertisers(): Promise<Map<string, PlatformAdvertiser[]>> {
    const response = await fetch(`${BACKEND_URL}/api/admin/advertisers/all`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
      },
    })
    if (!response.ok) {
      let detail = 'Error fetching all advertisers'
      try {
        const err = await response.json()
        detail = (err as ErrorResponse).detail || detail
      } catch {}
      throw new Error(detail)
    }
    const data = await response.json()
    if (!Array.isArray(data)) {
      throw new Error('Unexpected response shape from advertisers endpoint')
    }
    // Group by brand_id
    const grouped = new Map<string, PlatformAdvertiser[]>()
    for (const adv of data as PlatformAdvertiser[]) {
      if (!grouped.has(adv.brand_id)) {
        grouped.set(adv.brand_id, [])
      }
      grouped.get(adv.brand_id)!.push(adv)
    }
    return grouped
  }

  async function setAdvertiserSector(
    brandId: string,
    advertiserId: string,
    sectorId: string,
    method: 'POST' | 'DELETE',
  ): Promise<PlatformAdvertiser> {
    const res = await fetch(
      `${BACKEND_URL}/api/admin/brands/${brandId}/advertisers/${advertiserId}/sectors/${sectorId}`,
      {
        credentials: 'include',
        headers: { Authorization: `Bearer ${auth.stytchToken}` },
        method,
      },
    )
    const data = await res.json()
    if (!res.ok) {
      throw new Error((data as ErrorResponse).detail || 'Error updating advertiser sector')
    }
    return data as PlatformAdvertiser
  }

  function addAdvertiserSector(brandId: string, advertiserId: string, sectorId: string) {
    return setAdvertiserSector(brandId, advertiserId, sectorId, 'POST')
  }

  // TODO: Currently unused - remove if not needed for future use
  function removeAdvertiserSector(brandId: string, advertiserId: string, sectorId: string) {
    return setAdvertiserSector(brandId, advertiserId, sectorId, 'DELETE')
  }

  async function excludeAdvertiserSector(
    brandId: string,
    advertiserId: string,
    sectorId: string,
  ): Promise<PlatformAdvertiser> {
    const res = await fetch(
      `${BACKEND_URL}/api/admin/brands/${brandId}/advertisers/${advertiserId}/sectors/${sectorId}/exclude`,
      {
        credentials: 'include',
        headers: { Authorization: `Bearer ${auth.stytchToken}` },
        method: 'POST',
      },
    )
    const data = await res.json()
    if (!res.ok) {
      throw new Error((data as ErrorResponse).detail || 'Error excluding sector')
    }
    return data as PlatformAdvertiser
  }

  async function unexcludeAdvertiserSector(
    brandId: string,
    advertiserId: string,
    sectorId: string,
  ): Promise<PlatformAdvertiser> {
    const res = await fetch(
      `${BACKEND_URL}/api/admin/brands/${brandId}/advertisers/${advertiserId}/sectors/${sectorId}/exclude`,
      {
        credentials: 'include',
        headers: { Authorization: `Bearer ${auth.stytchToken}` },
        method: 'DELETE',
      },
    )
    const data = await res.json()
    if (!res.ok) {
      throw new Error((data as ErrorResponse).detail || 'Error removing exclusion')
    }
    return data as PlatformAdvertiser
  }

  async function createBrandAdvertiser(
    brandId: string,
    request: CreatePlatformAdvertiserRequest,
  ): Promise<PlatformAdvertiser> {
    return await fetch(`${BACKEND_URL}/api/admin/brands/${brandId}/advertisers`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify(request),
    }).then(async (res) => {
      const data = await res.json()
      if (!res.ok) {
        throw new Error((data as ErrorResponse).detail || 'Error creating advertiser')
      }
      return data as PlatformAdvertiser
    })
  }

  async function updateBrandAdvertiser(
    brandId: string,
    advertiserId: string,
    request: UpdatePlatformAdvertiserRequest,
  ): Promise<PlatformAdvertiser> {
    return await fetch(`${BACKEND_URL}/api/admin/brands/${brandId}/advertisers/${advertiserId}`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
        'Content-Type': 'application/json',
      },
      method: 'PATCH',
      body: JSON.stringify(request),
    }).then(async (res) => {
      const data = await res.json()
      if (!res.ok) {
        throw new Error((data as ErrorResponse).detail || 'Error updating advertiser')
      }
      return data as PlatformAdvertiser
    })
  }

  async function deleteBrandAdvertiser(brandId: string, advertiserId: string): Promise<void> {
    return await fetch(`${BACKEND_URL}/api/admin/brands/${brandId}/advertisers/${advertiserId}`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
      },
      method: 'DELETE',
    }).then(async (res) => {
      if (!res.ok) {
        const data = await res.json()
        throw new Error((data as ErrorResponse).detail || 'Error deleting advertiser')
      }
    })
  }

  // =========================================================================
  // Organization Subscriptions
  // =========================================================================

  async function fetchOrgPlatformSubscriptions(
    organizationId: string,
  ): Promise<PlatformSubscription[]> {
    return await fetch(
      `${BACKEND_URL}/api/admin/organizations/${organizationId}/subscriptions/platforms`,
      {
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${auth.stytchToken}`,
        },
      },
    ).then(async (res) => {
      const data = await res.json()
      if (!res.ok) {
        throw new Error((data as ErrorResponse).detail || 'Error fetching platform subscriptions')
      }
      return data as PlatformSubscription[]
    })
  }

  async function updateOrgPlatformSubscriptions(
    organizationId: string,
    platforms: { platform_id: string; is_visible?: boolean }[],
  ): Promise<PlatformSubscription[]> {
    return await fetch(
      `${BACKEND_URL}/api/admin/organizations/${organizationId}/subscriptions/platforms`,
      {
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${auth.stytchToken}`,
          'Content-Type': 'application/json',
        },
        method: 'PUT',
        body: JSON.stringify({ platforms }),
      },
    ).then(async (res) => {
      const data = await res.json()
      if (!res.ok) {
        throw new Error((data as ErrorResponse).detail || 'Error updating platform subscriptions')
      }
      return data as PlatformSubscription[]
    })
  }

  async function fetchOrgCountrySubscriptions(
    organizationId: string,
  ): Promise<CountrySubscription[]> {
    return await fetch(
      `${BACKEND_URL}/api/admin/organizations/${organizationId}/subscriptions/countries`,
      {
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${auth.stytchToken}`,
        },
      },
    ).then(async (res) => {
      const data = await res.json()
      if (!res.ok) {
        throw new Error((data as ErrorResponse).detail || 'Error fetching country subscriptions')
      }
      return data as CountrySubscription[]
    })
  }

  async function updateOrgCountrySubscriptions(
    organizationId: string,
    countries: { country_id: string; is_visible?: boolean }[],
  ): Promise<CountrySubscription[]> {
    return await fetch(
      `${BACKEND_URL}/api/admin/organizations/${organizationId}/subscriptions/countries`,
      {
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${auth.stytchToken}`,
          'Content-Type': 'application/json',
        },
        method: 'PUT',
        body: JSON.stringify({ countries }),
      },
    ).then(async (res) => {
      const data = await res.json()
      if (!res.ok) {
        throw new Error((data as ErrorResponse).detail || 'Error updating country subscriptions')
      }
      return data as CountrySubscription[]
    })
  }

  async function fetchOrgBrandSubscriptions(organizationId: string): Promise<BrandSubscription[]> {
    return await fetch(
      `${BACKEND_URL}/api/admin/organizations/${organizationId}/subscriptions/brands`,
      {
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${auth.stytchToken}`,
        },
      },
    ).then(async (res) => {
      const data = await res.json()
      if (!res.ok) {
        throw new Error((data as ErrorResponse).detail || 'Error fetching brand subscriptions')
      }
      return data as BrandSubscription[]
    })
  }

  async function updateOrgBrandSubscriptions(
    organizationId: string,
    brands: { brand_id: string; is_owner: boolean; is_visible?: boolean }[],
  ): Promise<BrandSubscription[]> {
    return await fetch(
      `${BACKEND_URL}/api/admin/organizations/${organizationId}/subscriptions/brands`,
      {
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${auth.stytchToken}`,
          'Content-Type': 'application/json',
        },
        method: 'PUT',
        body: JSON.stringify({ brands }),
      },
    ).then(async (res) => {
      const data = await res.json()
      if (!res.ok) {
        throw new Error((data as ErrorResponse).detail || 'Error updating brand subscriptions')
      }
      return data as BrandSubscription[]
    })
  }

  async function updateBrandOwnership(
    organizationId: string,
    brandId: string,
    isOwner: boolean,
  ): Promise<BrandSubscription> {
    return await fetch(
      `${BACKEND_URL}/api/admin/organizations/${organizationId}/subscriptions/brands/${brandId}`,
      {
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${auth.stytchToken}`,
          'Content-Type': 'application/json',
        },
        method: 'PATCH',
        body: JSON.stringify({ is_owner: isOwner }),
      },
    ).then(async (res) => {
      const data = await res.json()
      if (!res.ok) {
        throw new Error((data as ErrorResponse).detail || 'Error updating brand ownership')
      }
      return data as BrandSubscription
    })
  }

  return {
    organizations,
    fetchOrganization,
    fetchOrganizations,
    createOrganization,
    updateOrganization,
    deleteOrganization,
    toggleOrganizationActive,
    fetchOrganizationDashboards,
    updateOrganizationDashboards,
    saveOrganizationLogo,
    deleteOrganizationLogo,
    inviteUsers,
    fetchOrganizationMembers,
    deleteOrganizationMember,
    resendMemberInvite,
    fetchOrganizationHomepage,
    updateOrganizationHomepage,
    deleteOrganizationHomepage,
    // Data entities
    platforms,
    getSubplatforms,
    getPlatformFamilyCodes,
    countries,
    countryGroups,
    brands,
    sectors,
    fetchPlatforms,
    fetchCountries,
    fetchCountryGroups,
    invalidateCache,
    fetchBrands,
    fetchBrand,
    createBrand,
    updateBrand,
    addBrandSector,
    removeBrandSector,
    deleteBrand,
    saveBrandLogo,
    deleteBrandLogo,
    importMetaLogo,
    // Sectors
    fetchSectors,
    createSector,
    updateSector,
    deleteSector,
    fetchSectorBrands,
    // Platform advertisers
    fetchBrandAdvertisers,
    fetchAllAdvertisers,
    createBrandAdvertiser,
    updateBrandAdvertiser,
    deleteBrandAdvertiser,
    addAdvertiserSector,
    removeAdvertiserSector,
    excludeAdvertiserSector,
    unexcludeAdvertiserSector,
    // Organization subscriptions
    fetchOrgPlatformSubscriptions,
    updateOrgPlatformSubscriptions,
    fetchOrgCountrySubscriptions,
    updateOrgCountrySubscriptions,
    fetchOrgBrandSubscriptions,
    updateOrgBrandSubscriptions,
    updateBrandOwnership,
  }
})
